import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Badge, Button, Card, CardBody, Col, FormGroup, Input, Label, Modal, ModalBody,
  ModalHeader, Row, Spinner, Table,
} from "reactstrap";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { reanalyseVideoCallQaClass,
  VideoCallQaLesson,
  VideoCallQaReport,
  VideoCallQaResult,
  getVideoCallQaLessons,
  getVideoCallQaReport,
} from "../../../Api/apiUtils";
import { VIDEOCALL_QA_BASEURL } from "../../../Api/api-constant";

// lesson states, in pipeline order; "queued" is set by the video call team's start-analysis call
const STATUSES = [
  "scheduled", "live", "recorded", "queued", "processing", "scored", "failed", "no_show", "cancelled",
];

const STATUS_COLOR: Record<string, string> = {
  scheduled: "light",
  live: "info",
  recorded: "secondary",
  queued: "warning",
  processing: "primary",
  scored: "success",
  failed: "danger",
  no_show: "dark",
  cancelled: "dark",
};

const SIDE_COLOR: Record<string, string> = {
  teacher: "danger",
  learner: "warning",
  platform: "info",
  unknown: "secondary",
};

const humanize = (key: string) => {
  const s = key.replace(/[_.]/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// timeline offsets are seconds from recording start
const clock = (s: number | null | undefined) => {
  if (s === null || s === undefined) return "";
  const total = Math.max(0, Math.round(s));
  const m = Math.floor(total / 60);
  return `${m}:${String(total % 60).padStart(2, "0")}`;
};

const when = (iso: string) => dayjs(iso).format("DD MMM YYYY, h:mm A");

const pct = (c: number | null) => (c === null ? "–" : `${Math.round(Number(c) * 100)}%`);

const errorMessage = (error: any): string => {
  const status: number | undefined = error?.response?.status;
  if (!error?.response) {
    return "Could not reach the Video Call QA service. Check your network, or ask the tech team whether this admin site's address is allowed by the service.";
  }
  if (status === 404) return "Lesson not found.";
  const detail = error.response.data?.detail;
  return typeof detail === "string" ? detail : `The Video Call QA service returned an error (${status}).`;
};

const StatusBadge = ({ status }: { status: string }) => (
  <Badge color={STATUS_COLOR[status] || "secondary"} className={status === "scheduled" ? "text-dark" : ""}>
    {humanize(status)}
  </Badge>
);

const ResultRow = ({ result }: { result: VideoCallQaResult }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = result.evidence.length > 0 || result.missing_elements.length > 0;
  return (
    <>
      <tr>
        <td className="fw-semibold">{humanize(result.parameter_key)}</td>
        <td className="text-nowrap">
          {result.score === null ? "–" : Number(result.score)}
          <span className="text-muted small"> / {result.scale}</span>
          {result.label && <div className="small text-muted">{result.label}</div>}
        </td>
        <td>{pct(result.confidence)}</td>
        <td className="small">{result.summary || <span className="text-muted">No summary</span>}</td>
        <td className="text-end">
          {hasDetail && (
            <Button color="link" size="sm" className="p-0" onClick={() => setOpen(!open)}>
              {open ? "Hide" : "Evidence"}
            </Button>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="bg-light small">
            {result.missing_elements.length > 0 && (
              <div className="mb-2">
                <span className="fw-semibold me-1">Missing:</span>
                {result.missing_elements.join(", ")}
              </div>
            )}
            {result.evidence.map((e, i) => (
              <div key={i} className="mb-1">
                <span className="text-muted me-2 font-monospace">
                  {clock(e.t_start)}
                  {e.t_end ? `–${clock(e.t_end)}` : ""}
                </span>
                {e.speaker && <span className="fw-semibold me-1">{humanize(e.speaker)}:</span>}
                {e.quote ? <q>{e.quote}</q> : e.frame_key ? <span className="text-muted">frame</span> : null}
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
};

/** Analysed call length as "1h 05m" / "29m 30s"; "–" until the class has been analysed. */
const callDuration = (s?: number | null) => {
  if (s === null || s === undefined) return "–";
  const total = Math.round(s);
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), sec = total % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${String(sec).padStart(2, "0")}s`;
};

const ReportModal = ({ lessonId, onClose }: { lessonId: string | null; onClose: () => void }) => {
  const [report, setReport] = useState<VideoCallQaReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;
    setReport(null);
    setError(null);
    getVideoCallQaReport(lessonId)
      .then((r) => !cancelled && setReport(r))
      .catch((e) => !cancelled && setError(errorMessage(e)));
    return () => {
      cancelled = true;
    };
  }, [lessonId]);

  const lesson = report?.live_class;
  const media = Object.entries(report?.media || {}).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Modal isOpen={!!lessonId} toggle={onClose} size="xl" scrollable>
      <ModalHeader toggle={onClose}>
        {lesson ? `Class ${lesson.class_schedule_id}` : "Lesson report"}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {!report && !error && (
          <div className="text-center py-5">
            <Spinner />
          </div>
        )}
        {lesson && report && (
          <>
            <div className="d-flex flex-wrap gap-4 mb-4 small">
              <div>
                <div className="text-muted">Status</div>
                <StatusBadge status={lesson.status} />
              </div>
              <div>
                <div className="text-muted">Scheduled</div>
                {when(lesson.scheduled_start)} – {dayjs(lesson.scheduled_end).format("h:mm A")}
              </div>
              <div>
                <div className="text-muted">Type</div>
                {humanize(lesson.class_type)}
              </div>
              <div>
                <div className="text-muted">Instrument</div>
                {lesson.instrument || "–"}
              </div>
            </div>

            <h6>Scores</h6>
            {report.results.length === 0 ? (
              <p className="text-muted small">
                {lesson.status === "scored"
                  ? "No parameters were scored for this lesson."
                  : "Not scored yet. Scores appear once analysis finishes."}
              </p>
            ) : (
              <Table size="sm" responsive className="mb-4">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>Parameter</th>
                    <th style={{ width: "12%" }}>Score</th>
                    <th style={{ width: "10%" }}>Confidence</th>
                    <th>Summary</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {report.results
                    .slice()
                    .sort((a, b) => a.parameter_key.localeCompare(b.parameter_key))
                    .map((r) => (
                      <ResultRow key={r.id} result={r} />
                    ))}
                </tbody>
              </Table>
            )}

            <h6>Technical incidents</h6>
            {report.incidents.length === 0 ? (
              <p className="text-muted small">No incidents recorded.</p>
            ) : (
              <Table size="sm" responsive className="mb-4">
                <thead>
                  <tr>
                    <th>Incident</th>
                    <th>Side</th>
                    <th>Cause</th>
                    <th>When</th>
                    <th>Time lost</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {report.incidents.map((i) => (
                    <tr key={i.id}>
                      <td>{humanize(i.kind)}</td>
                      <td>
                        <Badge color={SIDE_COLOR[i.side] || "secondary"}>{humanize(i.side)}</Badge>
                      </td>
                      <td className="small">{i.cause ? humanize(i.cause) : "–"}</td>
                      <td className="font-monospace small">
                        {clock(i.t_start_s)}
                        {i.t_end_s !== null ? `–${clock(i.t_end_s)}` : ""}
                      </td>
                      <td>{i.lost_seconds !== null ? `${Math.round(Number(i.lost_seconds))}s` : "–"}</td>
                      <td>{pct(i.confidence)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <h6>Recordings</h6>
            {media.length === 0 ? (
              <p className="text-muted small mb-0">No recording files registered.</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {media.map(([key, url]) => {
                  const [kind, uid] = key.split(":");
                  return (
                    <Button key={key} tag="a" href={url} target="_blank" rel="noreferrer" size="sm" outline>
                      {humanize(kind)}
                      {uid ? ` · uid ${uid}` : ""}
                    </Button>
                  );
                })}
                {report.timeline_url && (
                  <Button tag="a" href={report.timeline_url} target="_blank" rel="noreferrer" size="sm" outline>
                    Timeline JSON
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

const VideoCallQa = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [since, setSince] = useState(dayjs().subtract(7, "day").format("YYYY-MM-DD"));
  const [classId, setClassId] = useState("");
  const [lessons, setLessons] = useState<VideoCallQaLesson[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openLesson, setOpenLesson] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!VIDEOCALL_QA_BASEURL) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await getVideoCallQaLessons({
        status: status || undefined,
        since: since ? dayjs(since).startOf("day").toISOString() : undefined,
        limit: 200,
      });
      setLessons(rows);
    } catch (e) {
      setError(errorMessage(e));
      setLessons([]);
    } finally {
      setLoading(false);
    }
  }, [status, since]);

  useEffect(() => {
    load();
  }, [load]);

  const needle = classId.trim();
  const shown = needle ? lessons.filter((l) => l.class_schedule_id.includes(needle)) : lessons;

  return (
    <div className="page-body" style={{ padding: "16px" }}>
      <Card>
        <CardBody>
          <h4 className="mb-1">Video Call QA</h4>
          <p className="text-muted mb-3">
            AI quality reports for recorded live classes: academic and behaviour scores with evidence,
            and technical incidents. A class appears here once its recording has been sent for analysis.
          </p>

          {!VIDEOCALL_QA_BASEURL ? (
            <Alert color="warning" className="mb-0">
              Video Call QA is not set up on this environment (REACT_APP_VIDEOCALL_QA_BASEURL is empty).
            </Alert>
          ) : (
            <>
              <Row className="g-3 align-items-end mb-3">
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label for="vcqa-status" className="small">Status</Label>
                    <Input id="vcqa-status" type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="">All</option>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{humanize(s)}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label for="vcqa-since" className="small">Scheduled from</Label>
                    <Input id="vcqa-since" type="date" value={since} onChange={(e) => setSince(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup className="mb-0">
                    <Label for="vcqa-class" className="small">Class ID</Label>
                    <Input
                      id="vcqa-class"
                      placeholder="Filter by class ID"
                      value={classId}
                      onChange={(e) => setClassId(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3} className="text-md-end">
                  <Button color="primary" outline onClick={load} disabled={loading}>
                    {loading && <Spinner size="sm" className="me-2" />}Refresh
                  </Button>
                </Col>
              </Row>

              {error && <Alert color="danger">{error}</Alert>}

              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Class ID</th>
                    <th>Scheduled</th>
                    <th>Duration</th>
                    <th>Type</th>
                    <th>Instrument</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((l) => (
                    <tr key={l.id}>
                      <td className="font-monospace">{l.class_schedule_id}</td>
                      <td className="text-nowrap">{when(l.scheduled_start)}</td>
                      <td className="text-nowrap">{callDuration(l.call_duration_s)}</td>
                      <td>{humanize(l.class_type)}</td>
                      <td>{l.instrument || "–"}</td>
                      <td><StatusBadge status={l.status} /></td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          {l.call_duration_s != null && !["queued", "processing"].includes(l.status) && (
                            <Button
                              size="sm"
                              color="secondary"
                              outline
                              title="Run this class again with the current QA settings"
                              onClick={async () => {
                                if (!window.confirm(`Re-analyse class ${l.class_schedule_id} with the current QA settings? Its report updates when the run finishes; earlier results are kept.`)) return;
                                try {
                                  await reanalyseVideoCallQaClass(l.class_schedule_id);
                                  load();
                                } catch (e: any) {
                                  window.alert(`Could not start: ${e?.response?.data?.detail || e?.message}`);
                                }
                              }}
                            >
                              Re-analyse
                            </Button>
                          )}
                          <Button
                            size="sm"
                            color="primary"
                            onClick={() => navigate(`/pages/video-call-qa/review/${encodeURIComponent(l.class_schedule_id)}`)}
                          >
                            View report
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && shown.length === 0 && !error && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        No lessons match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {lessons.length === 200 && (
                <p className="small text-muted mt-2 mb-0">
                  Showing the latest 200 lessons. Narrow the date or status to see older ones.
                </p>
              )}
            </>
          )}
        </CardBody>
      </Card>
      <ReportModal lessonId={openLesson} onClose={() => setOpenLesson(null)} />
    </div>
  );
};

export default VideoCallQa;
